import { IdSchemaEnum } from "../../../Configurator/Enums/IdSchemaEnum";
import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import MiniGamesHttps from "../../../Https/MiniGamesHttps";
import ActivityService from "../../Activity/Service/ActivityService";
import { IEitherOrParticipationCallback } from "../Interfaces/MiniGamesInterfaces";
import ClassicQuizBasicModel from "../Models/ClassicQuiz/ClassicQuizBasicModel";
import ClassicQuizFilters from "../Models/ClassicQuiz/ClassicQuizFilters";
import ClassicQuizLeaderboardFilters from "../Models/ClassicQuiz/Rank/ClassicQuizLeaderboardFilters";
import ClassicQuizParticipationBodyModel from "../Models/ClassicQuiz/ClassicQuizParticipationBodyModel";
import ClassicQuizParticipationsFilters from "../Models/ClassicQuiz/ClassicQuizParticipationsFilters";
import ClassicQuizRankFilters from "../Models/ClassicQuiz/Rank/ClassicQuizRankFilters";
import EitherOrBasicModel from "../Models/EitherOr/EitherOrBasicModel";
import EitherOrFilters from "../Models/EitherOr/EitherOrFilters";
import EitherOrParticipationRequestModel from "../Models/EitherOr/Participation/EitherOrParticipationRequestModel";
import MiniGamesService from "../Service/MiniGamesService";
import PersonalityQuizFilters from "../Models/PersonalityQuiz/PersonalityQuizFilters";
import PersonalityQuizBasicModel from "../Models/PersonalityQuiz/PersonalityQuizBasicModel";
import PersonalityQuizParticipationBodyModel from "../Models/PersonalityQuiz/Participation/PersonalityQuizParticipationBodyModel";
import ClassicQuizUserParticipationsModel from "../Models/ClassicQuiz/ClassicQuizUserParticipationsModel";
import PersonalityQuizParticipationsFilters from "../Models/PersonalityQuiz/Participation/PersonalityQuizParticipationsFilters";
import ClassicQuizUserParticipationModel from "../Models/ClassicQuiz/ClassicQuizUserParticipationModel";

export default class MiniGamesFacade {
  readonly https: MiniGamesHttps = null;
  readonly service: MiniGamesService = null;
  readonly activityService: ActivityService = null;
  readonly idSchema: string = null;

  constructor(config: SDKConfigurationModel) {
    this.https = new MiniGamesHttps(config);
    this.service = new MiniGamesService(config);
    this.activityService = new ActivityService(config);
    this.idSchema = config.idSchema;
  }

  public getClassicQuizzes = async (
    filters?: ClassicQuizFilters,
    showFullContextData?: boolean,
    disableCache?: boolean
  ) => {
    if (filters) {
      filters = new ClassicQuizFilters(filters);
      await this.service.finalizeMiniGamesFilters(filters);
    }

    const response = await this.https.getClassicQuizzes(filters, disableCache);

    if (showFullContextData) {
      await this.service.setContextTagsModels(response.data);
    } else {
      // No need to show entity models for context tags. Remap only the context tag ids.
      for (let value of response.data) {
        await this.remapTags(value, IdSchemaEnum.NATIVE, this.idSchema);
      }
    }

    return response;
  };

  public getClassicQuizById = async (
    classicQuizId: string,
    includeCorrectOptionIds?: boolean,
    disableCache?: boolean
  ) => {
    const data = await this.https.getClassicQuizById(
      classicQuizId,
      includeCorrectOptionIds,
      disableCache
    );
    await this.service.setContextTagsModels([data]);

    return data;
  };

  public participateInClassicQuiz = async (
    classicQuizId: string,
    questions: ClassicQuizParticipationBodyModel[],
    includeCorrectOptionIds?: boolean
  ) => {
    return await this.https.participateInClassicQuiz(
      classicQuizId,
      questions,
      includeCorrectOptionIds
    );
  };

  public getMyClassicQuizParticipations = async (
    filters: ClassicQuizParticipationsFilters
  ): Promise<ClassicQuizUserParticipationModel[]> => {
    const includePagination = false;

    if (filters) {
      filters = new ClassicQuizParticipationsFilters(filters);
    }

    const participations = (await this.https.getMyClassicQuizParticipations(
      filters,
      includePagination
    )) as ClassicQuizUserParticipationModel[];
    const ids = participations.map((p) => p.classicQuizId);
    const models = await this.getClassicQuizzes({ classicQuizIds: ids });

    participations.forEach((p) => {
      p.classicQuizModel =
        models.data.find((m) => m.id === p.classicQuizId) || null;
    });

    return participations;
  };

  public getMyClassicQuizParticipationsWithPagination = async (
    filters: ClassicQuizParticipationsFilters
  ): Promise<ClassicQuizUserParticipationsModel> => {
    const includePagination = true;

    if (filters) {
      filters = new ClassicQuizParticipationsFilters(filters);
    }

    const participations = (await this.https.getMyClassicQuizParticipations(
      filters,
      includePagination
    )) as ClassicQuizUserParticipationsModel;
    const ids = participations.data.map((p) => p.classicQuizId);
    const models = await this.getClassicQuizzes({ classicQuizIds: ids });

    participations.data.forEach((p) => {
      p.classicQuizModel =
        models.data.find((m) => m.id === p.classicQuizId) || null;
    });

    return participations;
  };

  public getLeaderboardForClassicQuizzes = async (
    filters: ClassicQuizLeaderboardFilters,
    disableCache: boolean
  ) => {
    if (filters) {
      filters = new ClassicQuizLeaderboardFilters(filters);
    }

    const leaderboards = await this.https.getLeaderboardForClassicQuizzes(
      filters,
      disableCache
    );
    leaderboards.data =
      await this.service.setProfileModelsForClassicQuizLeaderboards(
        leaderboards.data
      );

    return leaderboards;
  };

  public getClassicQuizzesUserRanking = async (
    userId: string,
    filters: ClassicQuizRankFilters,
    disableCache: boolean
  ) => {
    if (filters) {
      filters = new ClassicQuizRankFilters(filters);
    }

    const rankings = await this.https.getClassicQuizzesUserRanking(
      userId,
      filters,
      disableCache
    );
    const classicQuizIds = rankings.data.map((r) => r.classicQuizId);
    const models = await this.getClassicQuizzes({ classicQuizIds });
    rankings.data = rankings.data.map((r) => {
      const classicQuizModel =
        models.data.find((m) => m.id === r.classicQuizId) || null;
      return {
        ...r,
        classicQuizModel,
      };
    });

    return rankings;
  };

  public getClassicQuizQuestionsReport = async (
    classicQuizId: string,
    includeCorrectOptionIds: boolean
  ) => {
    includeCorrectOptionIds = includeCorrectOptionIds || false;
    const response = await this.https.getClassicQuizQuestionsReport(
      classicQuizId
    );
    const classicQuiz = await this.getClassicQuizById(
      classicQuizId,
      includeCorrectOptionIds,
      true
    );

    return this.service.setQuestionModelsInQuestionsReport(
      response,
      classicQuiz
    );
  };

  public getClassicQuizResultsSummary = async (
    classicQuizId: string,
    disableCache: boolean
  ) => {
    return await this.https.getClassicQuizResultsSummary(
      classicQuizId,
      disableCache
    );
  };

  public getEitherOrs = async (
    filters?: EitherOrFilters,
    showFullContextData?: boolean,
    disableCache?: boolean
  ) => {
    if (filters) {
      filters = new EitherOrFilters(filters);
      await this.service.finalizeMiniGamesFilters(filters);
    }

    const response = await this.https.getEitherOrs(filters, disableCache);

    if (showFullContextData) {
      await this.service.setContextTagsModels(response.data);
    } else {
      // No need to show entity models for context tags. Remap only the context tag ids.
      for (let value of response.data) {
        await this.remapTags(value, IdSchemaEnum.NATIVE, this.idSchema);
      }
    }

    return response;
  };

  public getEitherOrById = async (
    eitherOrId: string,
    disableCache?: boolean
  ) => {
    const data = await this.https.getEitherOrById(eitherOrId, disableCache);
    await this.service.setContextTagsModels([data]);

    return data;
  };

  public getMyEitherOrStats = async (eitherOrId: string) => {
    return await this.https.getMyEitherOrStats(eitherOrId);
  };

  public getEitherOrResults = async (
    eitherOrId: string,
    disableCache?: boolean
  ) => {
    const response = await this.https.getEitherOrResults(
      eitherOrId,
      disableCache
    );

    return await this.service.setProfileModelsForEitherOrStandings(response);
  };

  public participateInEitherOr = async (
    eitherOrId: string,
    participation: EitherOrParticipationRequestModel | null,
    onEitherOrParticipationCallbacks?: IEitherOrParticipationCallback
  ) => {
    if (!onEitherOrParticipationCallbacks) {
      return await this.service.defaultEitherOrParticipation(
        eitherOrId,
        participation
      );
    }

    return await this.service.handleEitherOrParticipationWithCallback(
      eitherOrId,
      participation,
      onEitherOrParticipationCallbacks
    );
  };

  public calculateEitherOrResultPercentile = async (
    eitherOrId: string,
    points: number
  ) => {
    if (points === null || points === undefined || isNaN(points) || points < 0)
      return null;

    if (points === 0) return points;

    const result = await this.getEitherOrResults(eitherOrId, true);
    const totalUsers: number = result.breakdown.reduce(
      (acc, entry) => acc + entry.totalUsers,
      0
    );
    let cumulativeUsers: number = 0;

    for (const entry of result.breakdown) {
      cumulativeUsers += entry.totalUsers;
      if (
        points >= entry.rangeStartInclusive &&
        points <= entry.rangeEndInclusive
      ) {
        return parseFloat(((cumulativeUsers / totalUsers) * 100).toFixed(1));
      }
    }

    // If points fall outside the provided range
    return null;
  };

  public getPersonalityQuizzes = async (
    filters?: PersonalityQuizFilters,
    showFullContextData?: boolean,
    disableCache?: boolean
  ) => {
    if (filters) {
      filters = new PersonalityQuizFilters(filters);
      await this.service.finalizeMiniGamesFilters(filters);
    }

    const response = await this.https.getPersonalityQuizzes(
      filters,
      disableCache
    );

    if (showFullContextData) {
      await this.service.setContextTagsModels(response.data);
    } else {
      // No need to show entity models for context tags. Remap only the context tag ids.
      for (let value of response.data) {
        await this.remapTags(value, IdSchemaEnum.NATIVE, this.idSchema);
      }
    }

    return response;
  };

  public getPersonalityQuizById = async (
    personalityQuizId: string,
    disableCache?: boolean
  ) => {
    const data = await this.https.getPersonalityQuizById(
      personalityQuizId,
      disableCache
    );
    await this.service.setContextTagsModels([data]);

    return data;
  };

  public participateInPersonalityQuiz = async (
    personalityQuizId: string,
    questions: PersonalityQuizParticipationBodyModel[]
  ) => {
    return await this.https.participateInPersonalityQuiz(
      personalityQuizId,
      questions
    );
  };

  public getPersonalityQuizUserParticipation = async (
    personalityQuizId: string,
    profileId: string,
    disableCache?: boolean
  ) => {
    return await this.https.getPersonalityQuizUserParticipation(
      personalityQuizId,
      profileId,
      disableCache
    );
  };

  public getMyPersonalityQuizParticipations = async (
    filters: PersonalityQuizParticipationsFilters
  ) => {
    if (filters) {
      filters = new PersonalityQuizParticipationsFilters(filters);
    }

    return await this.https.getMyPersonalityQuizParticipations(filters);
  };

  private remapTags = async (
    object:
      | ClassicQuizBasicModel
      | EitherOrBasicModel
      | PersonalityQuizBasicModel,
    fromSchema,
    toSchema
  ) => {
    if (
      this.idSchema != IdSchemaEnum.NATIVE &&
      object.context &&
      object.context.tags &&
      object.context.tags.length
    ) {
      const copyTags = JSON.parse(JSON.stringify(object.context.tags));
      const remapIdsTags = await this.activityService.remapTagsIds(
        copyTags,
        fromSchema,
        toSchema
      );
      //@ts-ignore
      object.context.tags = remapIdsTags;
    }
  };
}
